import themeGet from "@styled-system/theme-get";
import { Icon } from "orcs-design-system";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { useTheme } from "styled-components";

import DesignSystemTree from "./adapters/designSystemTree";
import { toTreeData } from "./adaptTreeData";
import type {
  ArboristTreeApi,
  CustomizeTreeHeaderFn,
  TreeNodeData,
  TreePrimitiveNode,
  TreeViewNextProps,
} from "./treeTypes";

type AppTheme = {
  appScale?: {
    treeRowHeight?: string | number;
    treeIndent?: string | number;
  };
};

/** Same as ORCS TreeNav: avoid passing NaN into the tree when theme tokens are missing. */
const parsePx = (v: string | number | null | undefined): number | null => {
  if (v == null) {
    return null;
  }
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? null : n;
};

const DEFAULT_TREE_ROW_HEIGHT = 32;
/** Align with treebeard `subtree.paddingLeft` in `TreeView.json` (16px per level). */
const DEFAULT_TREE_INDENT = 16;

function findSourceNodeById<T extends TreeNodeData>(
  nodes: readonly T[],
  id: string
): T | undefined {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    const children = node.children;
    if (Array.isArray(children) && children.length > 0) {
      const found = findSourceNodeById(children as T[], id);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
}

function collectOpenStateFromNodes<T extends TreeNodeData>(
  nodes: readonly T[],
  getOpenState: (node: T) => boolean | undefined,
  acc: Record<string, boolean> = {}
): Record<string, boolean> {
  for (const node of nodes) {
    const shouldOpen = getOpenState(node);
    if (shouldOpen !== undefined) {
      acc[node.id] = shouldOpen;
    }
    const children = node.children;
    if (Array.isArray(children) && children.length > 0) {
      collectOpenStateFromNodes(children as T[], getOpenState, acc);
    }
  }
  return acc;
}

function countVisibleTreeNodes<T extends TreeNodeData>(
  nodes: readonly T[],
  getOpenState: ((node: T) => boolean | undefined) | undefined,
  openByDefault: boolean
): number {
  let count = 0;
  for (const node of nodes) {
    count += 1;
    const children = node.children;
    if (!Array.isArray(children) || children.length === 0) {
      continue;
    }
    const hint = getOpenState?.(node);
    const isOpen = hint !== undefined ? hint : openByDefault;
    if (isOpen) {
      count += countVisibleTreeNodes(
        children as T[],
        getOpenState,
        openByDefault
      );
    }
  }
  return count;
}

function applyOpenStateToTree<T extends TreeNodeData>(
  api: ArboristTreeApi<T>,
  nodes: readonly T[],
  getOpenState: (node: T) => boolean | undefined
): boolean {
  if (!api.open || !api.close) {
    return false;
  }

  let changed = false;

  for (const node of nodes) {
    const shouldOpen = getOpenState(node);
    if (shouldOpen !== undefined) {
      if (shouldOpen) {
        if (api.isOpen?.(node.id) !== true) {
          api.open(node.id, false);
          changed = true;
        }
      } else if (api.isOpen?.(node.id) === true) {
        api.close(node.id, false);
        changed = true;
      }
    }
    const children = node.children;
    if (Array.isArray(children) && children.length > 0) {
      changed =
        applyOpenStateToTree(api, children as T[], getOpenState) || changed;
    }
  }

  if (changed) {
    api.redrawList?.();
  }

  return changed;
}

const TreeViewWrapper = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  /* ORCS token-based spacing for TreeView containers used in sidenav panels. */
  padding-left: ${themeGet("space.s")};
  padding-right: ${themeGet("space.s")};
`;

/* Legacy treebeard TreeHeader: full-width hover/active hit area in scroll parents */
const TreeRow = styled.div<{
  $isActive: boolean;
  $isDisabled?: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${themeGet("appScale.treeRowHeight")};
  height: ${themeGet("appScale.treeRowHeight")};
  max-height: ${themeGet("appScale.treeRowHeight")};
  overflow: hidden;
  cursor: ${(props) => (props.$isDisabled ? "default" : "pointer")};
  border-radius: ${themeGet("radii.2")};
  padding: ${themeGet("space.xs")} ${themeGet("space.s")};
  /* Avoid left-edge clipping in scroll containers while keeping right-edge bleed. */
  margin: 0 -${themeGet("space.s")} 0 0;
  transition: ${themeGet("transition.transitionDefault")};
  ${(props) =>
    !props.$isDisabled &&
    `
    &:hover {
      background-color: ${themeGet("colors.hoverSurface")(props)};
    }
  `}
  ${(props) =>
    props.$isActive &&
    `
    background-color: ${themeGet("colors.primaryLightest")(props)};
    &:hover { background-color: ${themeGet("colors.primaryLightest")(props)}; }
  `}
  [data-tree-row-content] {
    color: ${themeGet("colors.bodyText")};
  }
  ${(props) =>
    !props.$isDisabled &&
    `
    &:hover [data-tree-row-content],
    &[data-active="true"] [data-tree-row-content] {
      color: ${themeGet("colors.primary")(props)};
    }
  `}
`;

/** Matches treebeard `TreeView.json` toggle.base (24×24 hit target). */
const TOGGLE_SLOT_PX = 24;
/** Narrow slot when the row has no expand chevron (aligns label with legacy treebeard). */
const LEAF_GUTTER_PX = 11;

function isPlaceholderOnlyChildren(children: unknown): boolean {
  if (!Array.isArray(children) || children.length === 0) {
    return false;
  }
  return children.every(
    (c) =>
      c != null &&
      typeof c === "object" &&
      (c as { isPlaceholder?: boolean }).isPlaceholder === true
  );
}

function rowShowsExpandToggle<T extends TreeNodeData>(
  node: TreePrimitiveNode<T>,
  data: T
): boolean {
  if ((data as { isExpandableBranch?: boolean }).isExpandableBranch === true) {
    return true;
  }
  if (isPlaceholderOnlyChildren(data.children)) {
    return false;
  }
  return !node.isLeaf;
}

const ExpandChrome = styled.div<{ $wide: boolean }>`
  flex-shrink: 0;
  align-self: center;
  box-sizing: border-box;
  height: ${TOGGLE_SLOT_PX}px;
  margin-left: 0;
  margin-right: ${themeGet("space.s")};
  width: ${({ $wide }) => ($wide ? TOGGLE_SLOT_PX : LEAF_GUTTER_PX)}px;
  min-width: ${({ $wide }) => ($wide ? TOGGLE_SLOT_PX : LEAF_GUTTER_PX)}px;
  transition:
    width 180ms ease-in-out,
    min-width 180ms ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ToggleButton = styled.button<{ $isOpen: boolean }>`
  border: none;
  background: none;
  appearance: none;
  box-sizing: border-box;
  width: 100%;
  height: ${TOGGLE_SLOT_PX}px;
  padding: ${themeGet("space.xs")};
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: visible;
`;

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
  overflow: visible;
  transition: ${themeGet("transition.transitionDefault")};
`;

function DefaultNodeContent<T extends TreeNodeData>({
  node,
}: {
  node: TreePrimitiveNode<T>;
}) {
  return (
    <span
      data-testid="tree-view-next-node-name"
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
      }}
    >
      {node.data.name}
    </span>
  );
}

/** react-arborist DefaultRow does parseFloat(undefined)+padding → NaN top; rows stack. */
function rowTopPx(
  listStyle: React.CSSProperties | undefined,
  rowIndex: number,
  rowHeight: number
): number {
  const parsed = parseFloat(String(listStyle?.top ?? ""));
  if (Number.isFinite(parsed)) {
    return parsed;
  }
  return rowIndex * rowHeight;
}

type ArboristRowProps = {
  node: {
    handleClick: (e: React.MouseEvent) => void;
    rowIndex?: number | null | undefined;
  };
  attrs: React.HTMLAttributes<HTMLDivElement> & {
    style?: React.CSSProperties | undefined;
  };
  innerRef: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
};

function isRowActionTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(target.closest("[data-tree-row-action], [data-tree-toggle]"))
  );
}

function createVirtualizedRow(rowHeight: number) {
  return function VirtualizedRow({
    node,
    attrs,
    innerRef,
    children,
  }: ArboristRowProps) {
    const { style: listStyle, ...restAttrs } = attrs;
    const rowIndex =
      typeof node.rowIndex === "number" && node.rowIndex >= 0
        ? node.rowIndex
        : 0;
    return (
      <div
        {...restAttrs}
        ref={innerRef}
        style={{
          ...listStyle,
          top: rowTopPx(listStyle, rowIndex, rowHeight),
        }}
        onClick={(e) => {
          if (isRowActionTarget(e.target)) {
            return;
          }
          node.handleClick(e);
        }}
      >
        {children}
      </div>
    );
  };
}

const DEFAULT_TREE_WIDTH = 300;

function TreeViewNextBase<T extends TreeNodeData>({
  data,
  "data-testid": testId,
  className,
  indentPx,
  customizeTreeHeader,
  openByDefault = true,
  getNodeOpenState,
  onNodeSelected,
  onChevronToggle,
  suppressOnNodeSelectedWhenCollapsingBranch = false,
  toggleBranchOnRowClick = true,
  activeNodeId,
  isNodeActive,
  isNodeDisabled,
  scrollActiveNodeOnDataChange = false,
  scrollActiveNodeOnStructureChange = false,
  scrollActiveBlock = "center",
  scrollActiveBehavior = "smooth",
  openStateStructureKey,
  syncOpenStateOnDataChange = true,
  openStateSyncDelayMs = 32,
}: TreeViewNextProps<T>): React.ReactElement {
  const theme = useTheme() as AppTheme;
  const rowHeight =
    parsePx(theme?.appScale?.treeRowHeight) ?? DEFAULT_TREE_ROW_HEIGHT;
  const themeIndent =
    parsePx(theme?.appScale?.treeIndent) ?? DEFAULT_TREE_INDENT;
  const indent =
    typeof indentPx === "number" && !Number.isNaN(indentPx)
      ? indentPx
      : themeIndent;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const treeRef = useRef<ArboristTreeApi<T> | null>(null);
  // Use refs so inline arrow functions passed by callers don't recreate the Node component
  // class on every parent render (which would unmount/remount all arborist rows).
  const isNodeActiveRef = useRef(isNodeActive);
  isNodeActiveRef.current = isNodeActive;
  const isNodeDisabledRef = useRef(isNodeDisabled);
  isNodeDisabledRef.current = isNodeDisabled;
  const [containerWidth, setContainerWidth] = useState(DEFAULT_TREE_WIDTH);
  const treeData = useMemo(() => toTreeData(data), [data]);
  const sourceNodes = useMemo((): T[] => {
    if (!data) {
      return [];
    }
    return Array.isArray(data) ? data : [data];
  }, [data]);
  const getNodeOpenStateRef = useRef(getNodeOpenState);
  getNodeOpenStateRef.current = getNodeOpenState;
  const openByDefaultRef = useRef(openByDefault);
  openByDefaultRef.current = openByDefault;
  const onChevronToggleRef = useRef(onChevronToggle);
  onChevronToggleRef.current = onChevronToggle;
  const sourceNodesRef = useRef(sourceNodes);
  sourceNodesRef.current = sourceNodes;
  const openStateStructureKeyRef = useRef<string | undefined>(undefined);
  const lastScrolledStructureKeyRef = useRef<string | undefined>(undefined);
  const openStateManaged = typeof getNodeOpenState === "function";
  const openStateManagedRef = useRef(openStateManaged);
  openStateManagedRef.current = openStateManaged;
  const [managedOpenRevision, setManagedOpenRevision] = useState(0);
  const seededInitialOpenState = useRef<Record<string, boolean> | undefined>(
    undefined
  );
  if (
    openStateManaged &&
    seededInitialOpenState.current === undefined &&
    sourceNodes.length > 0 &&
    getNodeOpenState
  ) {
    seededInitialOpenState.current = collectOpenStateFromNodes(
      sourceNodes,
      getNodeOpenState
    );
  }
  const managedInitialOpenState =
    openStateManaged && getNodeOpenState
      ? collectOpenStateFromNodes(sourceNodes, getNodeOpenState)
      : undefined;
  const initialOpenState = openStateManaged
    ? managedInitialOpenState
    : seededInitialOpenState.current;
  const treeRemountKey = openStateManaged
    ? `${openStateStructureKey ?? "managed"}:${managedOpenRevision}`
    : "unmanaged";

  const managedVisibleCountLive =
    openStateManaged && getNodeOpenState
      ? countVisibleTreeNodes(sourceNodes, getNodeOpenState, openByDefault)
      : null;

  const dataDerivedVisibleCount = useMemo(() => {
    if (!openStateManaged || !getNodeOpenState) {
      return null;
    }
    // Re-run when managed open hints change without a new data reference.
    void openStateStructureKey;
    void managedOpenRevision;
    return countVisibleTreeNodes(sourceNodes, getNodeOpenState, openByDefault);
  }, [
    openStateManaged,
    sourceNodes,
    getNodeOpenState,
    openByDefault,
    openStateStructureKey,
    managedOpenRevision,
  ]);

  const rootCount = Array.isArray(treeData)
    ? treeData.length
    : treeData
      ? 1
      : 0;

  // Height is data-driven: visibleNodes.length × rowHeight.
  // When open state is managed via `getNodeOpenState`, derive height synchronously
  // from node data to avoid a collapsed-then-expanded flash on updates.
  const [interactiveVisibleCount, setInteractiveVisibleCount] = useState(
    () => rootCount
  );
  const [arboristVisibleCount, setArboristVisibleCount] = useState(0);

  const syncArboristVisibleCount = useCallback(() => {
    requestAnimationFrame(() => {
      const count = treeRef.current?.visibleNodes.length ?? 0;
      if (count > 0) {
        setArboristVisibleCount(count);
        setInteractiveVisibleCount(count);
      }
    });
  }, []);
  const managedVisibleCountOverrideRef = useRef(0);
  const syncManagedVisibleCountFromData = useCallback(() => {
    const getOpen = getNodeOpenStateRef.current;
    if (!openStateManaged || !getOpen) {
      return;
    }
    const count = countVisibleTreeNodes(
      sourceNodesRef.current,
      getOpen,
      openByDefaultRef.current
    );
    if (count > 0) {
      managedVisibleCountOverrideRef.current = count;
      setInteractiveVisibleCount(count);
      setArboristVisibleCount(count);
    }
  }, [openStateManaged]);
  const syncManagedVisibleCountFromDataRef = useRef(
    syncManagedVisibleCountFromData
  );
  syncManagedVisibleCountFromDataRef.current = syncManagedVisibleCountFromData;
  const syncArboristVisibleCountRef = useRef(syncArboristVisibleCount);
  syncArboristVisibleCountRef.current = syncArboristVisibleCount;

  const onTreeToggle = useCallback((_id: string) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const count = treeRef.current?.visibleNodes.length ?? 0;
        if (count > 0) {
          setArboristVisibleCount(count);
          setInteractiveVisibleCount(count);
        }
      });
    });
  }, []);

  useLayoutEffect(() => {
    if (openStateManaged && (dataDerivedVisibleCount ?? 0) > 0) {
      managedVisibleCountOverrideRef.current = dataDerivedVisibleCount ?? 0;
    }

    const getOpen = getNodeOpenStateRef.current;
    const nodes = sourceNodesRef.current;
    if (openStateManaged && getOpen && nodes.length > 0) {
      const api = treeRef.current;
      if (api) {
        applyOpenStateToTree(api, nodes, getOpen);
      }
    }

    const count = treeRef.current?.visibleNodes.length ?? 0;
    if (count > 0) {
      setArboristVisibleCount(count);
      setInteractiveVisibleCount(count);
    } else if (openStateManaged && (dataDerivedVisibleCount ?? 0) > 0) {
      setInteractiveVisibleCount(dataDerivedVisibleCount ?? rootCount);
    }
  }, [treeData, dataDerivedVisibleCount, openStateManaged, rootCount]);

  // When `data` / `openByDefault` changes, re-sync list height after arborist applies
  // the new tree (double rAF: one frame was still short on lazy-expand + refreshTree).
  useEffect(() => {
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const count = treeRef.current?.visibleNodes.length ?? 0;
        if (count > 0) {
          setArboristVisibleCount(count);
          setInteractiveVisibleCount(count);
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [treeData, rootCount, openByDefault]);

  // Re-apply legacy treebeard `toggled` hints when structure changes, then
  // scroll the active row (triple rAF matches manage-tags treebeard parity).
  useEffect(() => {
    const getOpen = getNodeOpenStateRef.current;
    const nodes = sourceNodesRef.current;
    const hasSourceNodes = nodes.length > 0;
    const structureChanged =
      hasSourceNodes &&
      openStateStructureKey != null &&
      openStateStructureKey !== openStateStructureKeyRef.current;
    const shouldScrollOnDataChange =
      scrollActiveNodeOnDataChange && activeNodeId != null;
    if (hasSourceNodes && openStateStructureKey != null) {
      openStateStructureKeyRef.current = openStateStructureKey;
    }

    const shouldSyncOpenState =
      Boolean(getOpen) &&
      hasSourceNodes &&
      (syncOpenStateOnDataChange === true || structureChanged);

    if (!shouldSyncOpenState && !shouldScrollOnDataChange) {
      return;
    }

    let raf3 = 0;
    let raf2 = 0;
    let syncTimer: ReturnType<typeof setTimeout> | undefined;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (shouldSyncOpenState && getOpen) {
          const trySync = () => {
            const api = treeRef.current;
            if (!api) {
              return false;
            }
            applyOpenStateToTree(api, nodes, getOpen);
            if (!openStateManaged) {
              syncArboristVisibleCount();
            }
            return true;
          };

          syncTimer = setTimeout(() => {
            if (!trySync()) {
              raf3 = requestAnimationFrame(() => {
                trySync();
              });
            }
          }, openStateSyncDelayMs);
        }

        if (shouldScrollOnDataChange) {
          raf3 = requestAnimationFrame(() => {
            scrollActiveNodeInWrapperWithRetry(
              wrapperRef.current,
              scrollActiveBlock,
              0,
              48,
              undefined,
              true,
              scrollActiveBehavior
            );
          });
        }
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cancelAnimationFrame(raf3);
      if (syncTimer != null) {
        clearTimeout(syncTimer);
      }
    };
  }, [
    activeNodeId,
    openStateStructureKey,
    scrollActiveNodeOnDataChange,
    scrollActiveNodeOnStructureChange,
    scrollActiveBlock,
    scrollActiveBehavior,
    syncArboristVisibleCount,
    syncOpenStateOnDataChange,
    openStateManaged,
    openStateSyncDelayMs,
    treeData,
  ]);

  useEffect(() => {
    if (!scrollActiveNodeOnStructureChange || activeNodeId == null) {
      return;
    }
    if (openStateStructureKey == null) {
      return;
    }
    if (lastScrolledStructureKeyRef.current === openStateStructureKey) {
      return;
    }

    const keyToScroll = openStateStructureKey;
    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollActiveNodeInWrapperWithRetry(
            wrapperRef.current,
            scrollActiveBlock,
            0,
            60,
            () => {
              lastScrolledStructureKeyRef.current = keyToScroll;
            },
            true,
            scrollActiveBehavior
          );
        });
      });
    }, 160);

    return () => {
      clearTimeout(timer);
    };
  }, [
    activeNodeId,
    openStateStructureKey,
    scrollActiveNodeOnStructureChange,
    scrollActiveBlock,
    scrollActiveBehavior,
  ]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && typeof rect.width === "number" && rect.width > 0) {
        setContainerWidth(rect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const visibleCount = openStateManaged
    ? Math.max(
        managedVisibleCountLive ?? 0,
        managedVisibleCountOverrideRef.current,
        dataDerivedVisibleCount ?? 0,
        arboristVisibleCount,
        interactiveVisibleCount,
        rootCount
      )
    : Math.max(
        arboristVisibleCount ||
          dataDerivedVisibleCount ||
          interactiveVisibleCount ||
          0,
        rootCount
      );

  // Arborist can report 0 visible nodes for a frame after data arrives; keep a
  // non-zero height so virtualized rows (e.g. GroupsSearch dropdown) still paint.
  const computedTreeHeight = visibleCount * rowHeight;
  const effectiveHeight =
    rootCount > 0 && computedTreeHeight === 0 ? rowHeight : computedTreeHeight;

  // Selection-only scroll for trees that do not opt into scroll-on-data-change.
  useEffect(() => {
    if (activeNodeId == null || scrollActiveNodeOnDataChange) {
      return;
    }

    const raf = requestAnimationFrame(() => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }
      const target = wrapper.querySelector<HTMLElement>(
        '[data-scroll-target="true"]'
      );
      target?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => cancelAnimationFrame(raf);
  }, [activeNodeId, scrollActiveNodeOnDataChange]);

  const Node = useMemo(() => {
    const noopRefresh = () => {};
    const headerFn = customizeTreeHeader as
      | CustomizeTreeHeaderFn<T>
      | undefined;

    const TreeNode = React.memo(function TreeNode({
      node,
      style,
    }: {
      node: TreePrimitiveNode<T>;
      style: React.CSSProperties;
    }) {
      const showsExpandToggle = rowShowsExpandToggle(node, node.data);
      const isActive =
        typeof isNodeActiveRef.current === "function"
          ? isNodeActiveRef.current(node.data)
          : activeNodeId != null && node.data.id === activeNodeId;
      const isDisabled =
        typeof isNodeDisabledRef.current === "function"
          ? isNodeDisabledRef.current(node.data)
          : false;
      const runBranchToggle = (wasBranchOpen: boolean) => {
        const sourceNode =
          findSourceNodeById(sourceNodesRef.current, node.data.id) ?? node.data;
        onChevronToggleRef.current?.(sourceNode, !wasBranchOpen);
        if (openStateManagedRef.current) {
          setManagedOpenRevision((revision) => revision + 1);
        }
        node.toggle();
        const getOpen = getNodeOpenStateRef.current;
        const api = treeRef.current;
        if (getOpen && api) {
          applyOpenStateToTree(api, sourceNodesRef.current, getOpen);
        }
        syncArboristVisibleCountRef.current();
      };
      const runRowInteraction = () => {
        if (isDisabled) {
          return;
        }
        if (toggleBranchOnRowClick && showsExpandToggle) {
          const wasBranchOpen = node.isOpen;
          runBranchToggle(wasBranchOpen);
          const skipSelectOnCollapse =
            suppressOnNodeSelectedWhenCollapsingBranch === true &&
            wasBranchOpen &&
            isActive;
          if (!skipSelectOnCollapse) {
            onNodeSelected?.(node.data);
          }
          return;
        }
        onNodeSelected?.(node.data);
      };
      const runChevronInteraction = () => {
        if (isDisabled || !showsExpandToggle) {
          return;
        }
        if (toggleBranchOnRowClick) {
          runRowInteraction();
          return;
        }
        runBranchToggle(node.isOpen);
      };
      const headerContent =
        typeof headerFn === "function" ? (
          headerFn(
            { node: node.data },
            { refreshTree: noopRefresh, onActivate: runRowInteraction }
          )
        ) : (
          <DefaultNodeContent node={node} />
        );

      const isSingleSelection = activeNodeId != null;
      const isScrollTarget = isSingleSelection && isActive;

      return (
        <TreeRow
          style={style}
          data-testid={isScrollTarget ? "active-node-test" : undefined}
          data-active={isActive ? "true" : undefined}
          data-scroll-target={isScrollTarget ? "true" : undefined}
          $isActive={isActive}
          $isDisabled={isDisabled}
          onClick={
            isDisabled
              ? undefined
              : (e) => {
                  if (isRowActionTarget(e.target)) {
                    return;
                  }
                  runRowInteraction();
                }
          }
        >
          <ExpandChrome
            $wide={showsExpandToggle}
            {...(!showsExpandToggle ? { "aria-hidden": true } : {})}
          >
            {showsExpandToggle ? (
              <ToggleButton
                type="button"
                tabIndex={-1}
                data-tree-toggle
                aria-label={
                  node.isOpen ? "Collapse tree node" : "Expand tree node"
                }
                $isOpen={node.isOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  runChevronInteraction();
                }}
              >
                <Icon
                  icon={["fal", "chevron-right"]}
                  size="1x"
                  rotation={node.isOpen ? 90 : undefined}
                />
              </ToggleButton>
            ) : null}
          </ExpandChrome>
          <HeaderContent data-tree-row-content>{headerContent}</HeaderContent>
        </TreeRow>
      );
    });
    return TreeNode;
  }, [
    customizeTreeHeader,
    onNodeSelected,
    suppressOnNodeSelectedWhenCollapsingBranch,
    toggleBranchOnRowClick,
    activeNodeId,
    // isNodeActive, isNodeDisabled, and onChevronToggle are read via refs — excluded
    // from deps to prevent recreating the TreeNode component class when callers pass
    // inline arrow functions.
  ]);

  const renderRow = useMemo(() => createVirtualizedRow(rowHeight), [rowHeight]);

  return (
    <TreeViewWrapper
      ref={wrapperRef}
      data-testid={testId}
      className={className}
      style={{
        height: effectiveHeight,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <DesignSystemTree<T>
        key={treeRemountKey}
        data={treeData}
        openByDefault={openByDefault}
        initialOpenState={initialOpenState}
        selectionFollowsFocus
        disableMultiSelection
        rowHeight={rowHeight}
        indent={indent}
        overscanCount={5}
        width={containerWidth}
        height={effectiveHeight}
        treeRef={treeRef}
        renderRow={renderRow}
        onToggle={onTreeToggle}
      >
        {Node}
      </DesignSystemTree>
    </TreeViewWrapper>
  );
}

function findScrollableParent(elem: HTMLElement): HTMLElement | null {
  const fancyCardBody = elem.closest('[class*="FancyCardBody"]');
  if (fancyCardBody instanceof HTMLElement) {
    const fancyStyle = window.getComputedStyle(fancyCardBody);
    if (fancyStyle.overflowY === "auto" || fancyStyle.overflowY === "scroll") {
      return fancyCardBody;
    }
  }

  let parent = elem.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    if (style.overflowY === "auto" || style.overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
}

function findActiveScrollTarget(
  wrapper: HTMLElement,
  scopeToWrapper: boolean
): HTMLElement | null {
  const tagTypeRow =
    wrapper.querySelector<HTMLElement>(
      '[data-testid="tag-type-wrapper"]#active-node'
    ) ??
    wrapper.querySelector<HTMLElement>(
      '[data-testid="tag-type-wrapper"][data-scroll-target="true"]'
    );
  if (tagTypeRow) {
    return tagTypeRow.closest<HTMLElement>('[role="treeitem"]') ?? tagTypeRow;
  }

  const inWrapper =
    wrapper.querySelector<HTMLElement>(
      '[role="treeitem"][data-active="true"]'
    ) ??
    wrapper.querySelector<HTMLElement>('[data-scroll-target="true"]') ??
    wrapper.querySelector<HTMLElement>("#active-node");

  if (inWrapper || scopeToWrapper) {
    return inWrapper;
  }

  return (
    document.querySelector<HTMLElement>(
      '[data-testid="tag-type-wrapper"]#active-node'
    ) ??
    document.querySelector<HTMLElement>('[data-scroll-target="true"]') ??
    document.querySelector<HTMLElement>("#active-node")
  );
}

function scrollElementIntoScrollContainer(
  elem: HTMLElement,
  scrollableParent: HTMLElement,
  block: ScrollLogicalPosition,
  behavior: ScrollBehavior = "smooth"
): void {
  const parentRect = scrollableParent.getBoundingClientRect();
  const elemRect = elem.getBoundingClientRect();
  const { scrollTop } = scrollableParent;
  const elemPositionInContent = elemRect.top - parentRect.top + scrollTop;

  const blockOffset =
    block === "center"
      ? scrollableParent.clientHeight / 2 - elem.clientHeight / 2
      : block === "start"
        ? 0
        : scrollableParent.clientHeight - elem.clientHeight;

  scrollableParent.scrollTo({
    top: Math.max(0, elemPositionInContent - blockOffset),
    behavior,
  });
}

function scrollActiveNodeInWrapperWithRetry(
  wrapper: HTMLElement | null,
  block: ScrollLogicalPosition = "center",
  attempt = 0,
  maxAttempts = 48,
  onSuccess?: () => void,
  scopeToWrapper = false,
  behavior: ScrollBehavior = "smooth"
): void {
  if (!wrapper) {
    return;
  }

  const elem = findActiveScrollTarget(wrapper, scopeToWrapper);

  if (elem) {
    const scrollableParent =
      findScrollableParent(elem) ?? findScrollableParent(wrapper);
    if (scrollableParent) {
      scrollElementIntoScrollContainer(elem, scrollableParent, block, behavior);
    } else {
      elem.scrollIntoView({ behavior, block });
    }
    onSuccess?.();
    return;
  }

  if (attempt + 1 >= maxAttempts) {
    return;
  }

  requestAnimationFrame(() => {
    scrollActiveNodeInWrapperWithRetry(
      wrapper,
      block,
      attempt + 1,
      maxAttempts,
      onSuccess,
      scopeToWrapper,
      behavior
    );
  });
}

function scrollActiveNodeIntoView(
  containerOrBlock?: HTMLElement | ScrollLogicalPosition,
  block: ScrollLogicalPosition = "end",
  behavior: ScrollBehavior = "smooth"
): void {
  let container: HTMLElement | null = null;
  let scrollBlock = block;
  if (containerOrBlock instanceof HTMLElement) {
    container = containerOrBlock;
  } else if (typeof containerOrBlock === "string") {
    scrollBlock = containerOrBlock;
  }

  if (container) {
    scrollActiveNodeInWrapperWithRetry(
      container,
      scrollBlock,
      0,
      48,
      undefined,
      true,
      behavior
    );
    return;
  }

  scrollActiveNodeInWrapperWithRetry(
    document.body,
    scrollBlock,
    0,
    48,
    undefined,
    false,
    behavior
  );
}

const TreeViewNext = Object.assign(TreeViewNextBase, {
  scrollActiveNodeIntoView,
}) as typeof TreeViewNextBase & {
  scrollActiveNodeIntoView: typeof scrollActiveNodeIntoView;
};

export default TreeViewNext;
